// ─────────────────────────────────────────────────────────────────────────────
// Task A3 — Generador de Excel multi-hoja reutilizable para Reportería de Reconocimiento.
//
// Los 9 Excel de la spec de Reportería comparten el mismo patrón: "Resumen Ejecutivo +
// Definiciones + N hojas de detalle". Este módulo centraliza ese patrón para que las fases
// C/D/E/F/G (Generacional/Heatmap, Históricas/Campañas, Diario/Brillamos, Líderes, Moderación)
// solo tengan que construir sus hojas de detalle específicas y llamar a `exportMultiSheetExcel`.
//
// Task 11 — migrado de `xlsx` (SheetJS, sin soporte de estilos) a `exceljs`: el workbook se
// construye con `ExcelJS.Workbook` y toda hoja sin `styleHints` recibe el tema por omisión
// (`./excelTheme.ts`), medido de la plantilla real del cliente.
// ─────────────────────────────────────────────────────────────────────────────
import ExcelJS from 'exceljs';
import { EXCEL_FONT_FAMILY, excelColors, fillSolido } from './excelTheme';
import { descargarWorkbookComoExcel } from './excelDownload';

/** Valor de una celda: texto, número, o vacío (celda en blanco dentro de la fila). */
export type ExcelCellValue = string | number | null;

/** Una hoja del libro, ya con filas armadas (incluida la cabecera) en formato array-of-arrays. */
export interface ExcelSheetSpec {
  /** Nombre de la pestaña, ej. "Resumen Ejecutivo". */
  name: string;
  /** Filas ya armadas, incluida la fila de cabecera. */
  rows: ExcelCellValue[][];
  /** Filas (0-indexed) que llevan estilo de encabezado de sección (título/subtítulo). Sin esto, se
   *  aplica el estilo por defecto: fila 0 = título, fila 1 = subtítulo — el patrón que TODAS las
   *  hojas actuales ya siguen. */
  styleHints?: ExcelSheetStyleHints;
  /** Imagen estática (PNG) a insertar en la hoja — ver `ExcelImageHint`. */
  image?: ExcelImageHint;
}

/** Hints de estilo opcionales para una hoja — sin ellos se aplica el tema por omisión. */
export interface ExcelSheetStyleHints {
  /** Filas (0-indexed) de encabezado de tabla (fondo teal, texto blanco, centrado). */
  headerRows?: number[];
  /** Rangos de celdas a combinar, formato "A1:C1". */
  merges?: string[];
  /** Anchos de columna en el mismo orden que las columnas de `rows` (0-indexed). `undefined` en una
   *  posición dice "sin ancho medido en la plantilla real": la columna se queda con el ancho por
   *  omisión de Excel en vez de forzar un valor inventado (Tarea 16). */
  columnWidths?: Array<number | undefined>;
  /** Alturas de fila puntuales (título, subtítulo, encabezado con texto ajustado) — ver
   *  `ExcelRowHeightHint`. */
  rowHeights?: ExcelRowHeightHint[];
  /** Paleta de la hoja. Sin esto se usa `TEMA_EXCEL_POR_OMISION` (teal, medido de
   *  `02 - Campana brillamos juntos.xlsx`), que es el que llevan los 9 reportes salvo los que
   *  auditaron su propia plantilla — ver `ExcelSheetTheme`. */
  theme?: ExcelSheetTheme;
  /** Barras de datos nativas de Excel (Formato Condicional), ej. la columna "% del total" del
   *  Embudo de Adopción — ver `ExcelDataBarHint`. */
  dataBars?: ExcelDataBarHint[];
  /** Formato numérico nativo de Excel (`cell.numFmt`) por columna — necesario para que una barra de
   *  datos calcule su longitud sobre un NÚMERO real (ej. `0.4`) en vez de comparar el texto
   *  `"40.00%"` que el resto de las hojas usa (bug de producción 2026-08-26). */
  numberFormats?: Array<{ column: number; format: string }>;
  /** Fills de celda individuales — ver `ExcelCellFillHint`. Se aplican DESPUÉS del banding de
   *  `aplicarEstiloBase`, así que ganan sobre el zebra fila par/impar. */
  cellFills?: ExcelCellFillHint[];
  /** Color de TEXTO por celda puntual, ej. la columna «Estado» de Campañas Históricas — ver
   *  `ExcelCellColorHint`. */
  cellTextColors?: ExcelCellColorHint[];
  /** Filas pintadas de lado a lado aunque estén VACÍAS — ver `ExcelBandRowHint`. */
  bandRows?: ExcelBandRowHint[];
  /** Estilo por COLUMNA para las filas de datos — ver `ExcelDataColumnStyles`. */
  dataColumnStyles?: ExcelDataColumnStyles;
  /** Rejilla de bordes finos sobre un rango rectangular — ver `ExcelBorderedRangeHint`. */
  borderedRanges?: ExcelBorderedRangeHint[];
  /**
   * Reportería de Moderación (2026-09-03) — esta hoja calca la paleta PROPIA de
   * "07 - Moderacion.xlsx" (navys/cremas medidos de ese archivo), que no coincide con
   * `excelColors` (medido de `02 - Campana brillamos juntos.xlsx` para el resto de reportes). Con
   * `true`, `aplicarEstiloBase` deja de pintar fill/color de título, subtítulo, headerRows y
   * banding — la fuente (Aptos) y el tamaño base (10.5) SÍ se siguen aplicando, y `cellStyles`
   * (abajo) es entonces la ÚNICA fuente de color de la hoja.
   */
  isDefaultThemeSkipped?: boolean;
  /**
   * Estilo (fondo + texto) por celda puntual — la vía de escape para una hoja con
   * `isDefaultThemeSkipped`. Generaliza `ExcelCellColorHint` (que solo toca el texto): aquí una
   * celda puede fijar fondo, color de texto, negrita, cursiva y tamaño, todos opcionales. Se
   * aplica AL FINAL, después del resto de los hints — siempre gana sobre cualquier fill/font que ya
   * trajera la celda.
   */
  cellStyles?: ExcelCellStyleHint[];
}

/**
 * Paleta de UNA hoja: los mismos roles de color que `aplicarEstiloBase` ya pintaba con `excelColors`
 * fijo, ahora sustituibles por hoja. Existe porque las plantillas del cliente no comparten identidad
 * — `02 - Campana brillamos juntos.xlsx` es teal y `04 - Demografico.xlsx` es azul marino/crema — y
 * la alternativa (`isDefaultThemeSkipped` + un `cellStyles` por celda) cuesta O(filas × columnas)
 * objetos de hint por descarga sólo para repintar lo que el tema ya sabe pintar.
 */
export interface ExcelSheetTheme {
  tituloFondo: string;
  tituloTexto: string;
  subtituloFondo: string;
  subtituloTexto: string;
  headerTablaFondo: string;
  headerTablaTexto: string;
  filaPar: string;
  filaImpar: string;
  /** Tamaño en puntos del encabezado de tabla — omitido, 10 pt (el del tema por omisión). */
  headerFontSize?: number;
  /** Encabezados de tabla con "Ajustar texto": necesario cuando las columnas llevan nombres largos
   *  (los Fundamentales), que sin esto obligan a columnas de 40 caracteres de ancho. */
  isHeaderWrapped?: boolean;
  /** Subtítulo en cursiva, como lo pinta la plantilla del Demográfico. */
  isSubtituloItalic?: boolean;
}

/** Ver `ExcelSheetStyleHints.rowHeights`. */
export interface ExcelRowHeightHint {
  /** Fila 0-indexed dentro de `rows`. */
  row: number;
  /** Alto en puntos. */
  height: number;
}

/** Ver `ExcelSheetStyleHints.cellStyles`. */
export interface ExcelCellStyleHint {
  /** Fila 0-indexed dentro de `rows`. */
  row: number;
  /** Columna 0-indexed dentro de `rows`. */
  column: number;
  /** Color ARGB de fondo — omitido, no se toca el fill que ya tuviera la celda. */
  fillArgb?: string;
  /** Color ARGB del texto — omitido, no se toca. */
  fontColorArgb?: string;
  isBold?: boolean;
  isItalic?: boolean;
  /** Tamaño de fuente en puntos — omitido, se queda en el tamaño base (10.5). */
  fontSize?: number;
  /** Ajustar texto a la celda (equivalente a "Ajustar texto" de Excel) en vez de desbordarlo. */
  hasWrapText?: boolean;
  /** Alineación horizontal — omitida, se respeta la que trajera la celda. */
  horizontal?: ExcelJS.Alignment['horizontal'];
  /** Alineación vertical — omitida, se respeta la que trajera la celda. */
  vertical?: ExcelJS.Alignment['vertical'];
  /** Sangría en caracteres, el "relleno" del banner de una hoja (la plantilla usa 1). */
  indent?: number;
  /**
   * Formato numérico nativo de Excel para ESTA celda (`#,##0`, `0.00`, `0.0%`). A diferencia de
   * `numberFormats`, que fija el formato de una columna entera, aquí cada celda puede llevar el
   * suyo — necesario en la columna «Valor» del Resumen Ejecutivo, donde un conteo (`#,##0`), un
   * ratio (`0.00`) y un porcentaje (`0.0%`) comparten columna.
   */
  numFmt?: string;
}

/**
 * Una fila de color que cruza toda la hoja aunque no tenga texto — la banda amarilla bajo el
 * subtítulo de "06 - Lideres.xlsx". No puede salir de `cellFills`/`cellStyles` porque el loop de
 * `worksheet.eachRow` SALTA las filas vacías: el fill se pinta celda por celda con
 * `getRow().getCell()`, que sí las materializa.
 */
export interface ExcelBandRowHint {
  /** Fila 0-indexed dentro de `rows`. */
  row: number;
  fillArgb: string;
  /** Cuántas columnas cubre la banda, contadas desde la A. */
  columnCount: number;
  /** Color ARGB del texto de la banda — omitido, se queda con el color por omisión de la fuente. */
  fontColorArgb?: string;
  isItalic?: boolean;
  /** Tamaño en puntos — omitido, el base (10.5). */
  fontSize?: number;
}

/**
 * Estilo de UNA columna, aplicado a todas las filas de datos del rango.
 * `06 - Lideres.xlsx` no usa banding zebra: el color lo da la COLUMNA (texto en azul claro,
 * numéricas en blanco, «Participación» en crema), igual que su alineación (texto a la izquierda,
 * números a la derecha) y su formato numérico.
 */
export interface ExcelColumnStyleHint {
  /** Columna 0-indexed dentro de `rows`. */
  column: number;
  fillArgb?: string;
  fontColorArgb?: string;
  isBold?: boolean;
  horizontal?: ExcelJS.Alignment['horizontal'];
  vertical?: ExcelJS.Alignment['vertical'];
  hasWrapText?: boolean;
  /** Formato numérico nativo de Excel (`#,##0`, `0.0%`). */
  numFmt?: string;
}

/**
 * Ver `ExcelColumnStyleHint`. Declarar el estilo por columna y no por celda es lo que mantiene el
 * costo del hint en O(columnas) y no en O(filas x columnas): la hoja "Equipos por Líder" puede
 * traer 1 600+ líderes, y 16 000 objetos de hint por descarga no son gratis ni en memoria ni en
 * tiempo de serialización.
 */
export interface ExcelDataColumnStyles {
  /** Primera fila de datos, 0-indexed (la que va justo después del encabezado). */
  startRow: number;
  /** Última fila de datos, 0-indexed e INCLUSIVE. */
  endRow: number;
  columns: ExcelColumnStyleHint[];
}

/** Rejilla de bordes finos sobre un rango rectangular — ver `ExcelSheetStyleHints.borderedRanges`. */
export interface ExcelBorderedRangeHint {
  /** Filas/columnas 0-indexed, ambos extremos INCLUSIVE. */
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
  colorArgb: string;
}

/**
 * P18-ter (2026-09-02) — la columna «Estado» de «Campañas Históricas» pide un color de texto por
 * valor (Activa=verde, Expirada=rojo, Borrador=gris), a petición del cliente. Se referencia por
 * FILA/COLUMNA (no por el texto ya traducido de la celda, como haría un hint "valor → color"):
 * `t()` puede devolver un texto distinto según el idioma de quien exporta, y un hint por texto
 * dejaría de aplicar en cuanto el valor no fuera el literal en español. El caller, que SÍ conoce el
 * estado canónico de cada fila antes de traducirlo, arma este hint con el índice ya resuelto.
 */
export interface ExcelCellColorHint {
  /** Fila 0-indexed dentro de `rows` (la de encabezado cuenta como fila 0). */
  row: number;
  /** Columna 0-indexed dentro de `rows`. */
  column: number;
  /** Color ARGB del TEXTO de la celda (no el fondo). */
  colorArgb: string;
  /** Negrita además del color — opcional, default `false`. */
  bold?: boolean;
}

/**
 * Bug de producción 2026-08-26 (hoja "Embudo Adopción") — la plantilla de referencia del cliente
 * pinta la columna "% del total" con una barra de datos NATIVA de Excel (gradiente verde dentro de
 * la celda), no con texto plano. `exceljs` sí soporta esto vía Formato Condicional
 * (`worksheet.addConditionalFormatting`), a diferencia de gráficas nativas (no soportadas).
 */
export interface ExcelDataBarHint {
  /** Columna 0-indexed a la que aplica la barra (índice dentro de `rows`, no letra de Excel). */
  column: number;
  /** Fila 0-indexed de inicio del rango de datos (la primera fila de datos, después del header). */
  startRow: number;
  /** Fila 0-indexed de fin del rango de datos (inclusive). */
  endRow: number;
  /** Color ARGB de la barra (ej. `excelColors.dataBarVerde`). */
  color: string;
}

/**
 * Fill/fuente de UNA celda individual — usado para replicar en el Excel el mismo tinte de color por
 * Fundamental que ya pinta la pantalla (`fundamentalTintStyle`/`excelTintStyle`) en "Brecha
 * Generacional" y "Heatmap OU x Fundamental". A diferencia de `headerRows`/banding (uniforme por
 * fila), cada celda de estas dos hojas necesita un color DISTINTO según su columna (Fundamental) y su
 * valor relativo al máximo de esa columna.
 */
export interface ExcelCellFillHint {
  /** Fila 0-indexed dentro de `rows` (0 = cabecera; los datos empiezan en 1). */
  row: number;
  /** Columna 0-indexed dentro de `rows`. */
  column: number;
  fillArgb: string;
  fontArgb: string;
}

/**
 * Bug de producción 2026-08-26 (hoja "Distribución Fundamental") — la plantilla de referencia del
 * cliente acompaña la tabla con una gráfica de barras horizontales ("Reconocimientos por
 * Fundamental / Behavior"). `exceljs` NO soporta gráficas nativas de Excel (solo lee/ignora
 * chartsheets, no las escribe) — la única vía es insertar una IMAGEN estática (PNG), generada aquí
 * con Chart.js antes de exportar (ver `renderBarChartPng` en `chartToImage.ts`).
 */
export interface ExcelImageHint {
  /** PNG en base64 (sin el prefijo `data:image/png;base64,`). */
  base64: string;
  /** Celda superior-izquierda (0-indexed) donde empieza la imagen. */
  tl: { col: number; row: number };
  /** Ancho/alto en píxeles REALES del PNG (Tarea imagen Fundamental) — con `ext` en vez de un rango
   *  `tl`/`br`, la imagen conserva su proporción exacta en vez de estirarse al tamaño de las celdas
   *  que ocupe. */
  ext: { width: number; height: number };
}

/** Especificación completa de un reporte Excel multi-hoja. */
export interface ExcelReportSpec {
  fileName: string;
  sheets: ExcelSheetSpec[];
}

/** KPI mostrado en la hoja "Resumen Ejecutivo", en formato "KPI | Valor | Detalle". */
interface ResumenEjecutivoKpi {
  label: string;
  valor: string | number;
  detalle?: string;
}

/** Fila de la hoja "Definiciones": qué es cada dato y cómo se calcula. */
interface DefinicionDato {
  dato: string;
  queEs: string;
  comoSeCalcula: string;
  /**
   * Contra qué se lee ese número para que signifique algo ("las demás generaciones de la misma
   * tabla", "el total del periodo"). OPCIONAL a propósito: la plantilla del cliente de Demográfico
   * (`04 - Demografico.xlsx`) trae esta cuarta columna, pero los otros 8 Excel de Reportería se
   * auditaron contra plantillas de 3 columnas — sin este campo, la hoja sale exactamente igual que
   * antes para ellos, y la columna solo aparece cuando alguna definición la trae.
   */
  seComparaContra?: string;
}

/**
 * Tema que reciben las hojas que no declaran `styleHints.theme`: los mismos colores que
 * `aplicarEstiloBase` leía directo de `excelColors` antes de que el tema fuera sustituible, así que
 * los 8 reportes que no auditaron plantilla propia salen exactamente igual que antes.
 */
const TEMA_EXCEL_POR_OMISION: ExcelSheetTheme = {
  tituloFondo: excelColors.tituloFondo,
  tituloTexto: excelColors.tituloTexto,
  subtituloFondo: excelColors.subtituloFondo,
  subtituloTexto: excelColors.subtituloTexto,
  headerTablaFondo: excelColors.headerTablaFondo,
  headerTablaTexto: excelColors.headerTablaTexto,
  filaPar: excelColors.filaPar,
  filaImpar: excelColors.filaImpar,
};

const NOMBRE_HOJA_RESUMEN_EJECUTIVO = 'Resumen Ejecutivo';
const NOMBRE_HOJA_DEFINICIONES = 'Definiciones';
const EXTENSION_EXCEL = '.xlsx';
const FILA_TITULO = 0;
const FILA_SUBTITULO = 1;

/**
 * Normaliza el nombre de archivo del reporte agregando `.xlsx` si el caller lo omitió.
 * Mantiene la convención ya establecida en `exportExcel.ts` (`exportToExcel`/`exportSurveyToExcel`
 * agregan `.xlsx` automáticamente); sin este guard, un `fileName` sin extensión produce una
 * descarga sin extensión, en silencio, sin que TypeScript lo detecte.
 */
function normalizarNombreArchivo(fileName: string): string {
  return fileName.endsWith(EXTENSION_EXCEL) ? fileName : `${fileName}${EXTENSION_EXCEL}`;
}

/**
 * Aplica el tema por omisión a una hoja: fila 0 = título, fila 1 = subtítulo, filas de
 * `headerRows` = encabezado de tabla, y el resto en banding blanco/gris. También aplica los
 * `merges`/`columnWidths` de `styleHints`, si vienen.
 */
function aplicarEstiloBase(worksheet: ExcelJS.Worksheet, hints: ExcelSheetStyleHints | undefined) {
  // Si `headerRows` incluye la fila 0, esta hoja no tiene preámbulo de título/subtítulo (ej.
  // `buildDefinicionesSheet`, con `headerRows: [0]`): la fila 0 ES directamente el encabezado de
  // tabla, no hay título/subtítulo en ninguna fila de esta hoja. Sin este gate, la fila 1 (el primer
  // dato real, justo después del header) caía en la rama de subtítulo por coincidir con
  // `indiceFila === FILA_SUBTITULO`, en vez de recibir el banding filaPar/filaImpar que le
  // corresponde como cualquier otra fila de datos.
  const sinPreambulo = hints?.headerRows?.includes(FILA_TITULO) ?? false;
  const tema = hints?.theme ?? TEMA_EXCEL_POR_OMISION;

  worksheet.eachRow((row, rowNumber) => {
    const indiceFila = rowNumber - 1;
    row.eachCell({ includeEmpty: false }, (cell) => {
      cell.font = { name: EXCEL_FONT_FAMILY, size: 10.5 };
    });

    // Ver doc-comment de `ExcelSheetStyleHints.isDefaultThemeSkipped`: una hoja con paleta propia
    // (Reportería de Moderación) no pasa por título/subtítulo/headerRows/banding del tema
    // compartido — `cellStyles`, más abajo, es su única fuente de color.
    if (hints?.isDefaultThemeSkipped) return;

    if (hints?.headerRows?.includes(indiceFila)) {
      row.eachCell((cell) => {
        cell.fill = fillSolido(tema.headerTablaFondo);
        cell.font = {
          name: EXCEL_FONT_FAMILY, size: tema.headerFontSize ?? 10, bold: true,
          color: { argb: tema.headerTablaTexto },
        };
        cell.alignment = {
          horizontal: 'center', vertical: 'middle', wrapText: tema.isHeaderWrapped ?? false,
        };
      });
    } else if (!sinPreambulo && indiceFila === FILA_TITULO) {
      row.eachCell((cell) => {
        cell.fill = fillSolido(tema.tituloFondo);
        cell.font = { name: EXCEL_FONT_FAMILY, size: 18, bold: true, color: { argb: tema.tituloTexto } };
        cell.alignment = { vertical: 'middle' };
      });
    } else if (!sinPreambulo && indiceFila === FILA_SUBTITULO) {
      row.eachCell((cell) => {
        cell.fill = fillSolido(tema.subtituloFondo);
        cell.font = {
          name: EXCEL_FONT_FAMILY, size: 10, italic: tema.isSubtituloItalic ?? false,
          color: { argb: tema.subtituloTexto },
        };
        cell.alignment = { vertical: 'middle', wrapText: true };
      });
    } else if (indiceFila > (hints?.headerRows?.at(-1) ?? FILA_SUBTITULO)) {
      const esImpar = (indiceFila - ((hints?.headerRows?.at(-1) ?? FILA_SUBTITULO) + 1)) % 2 === 1;
      row.eachCell((cell) => {
        cell.fill = fillSolido(esImpar ? tema.filaImpar : tema.filaPar);
      });
    }
  });

  hints?.merges?.forEach((rango) => worksheet.mergeCells(rango));
  hints?.columnWidths?.forEach((ancho, indice) => {
    worksheet.getColumn(indice + 1).width = ancho;
  });
  hints?.rowHeights?.forEach(({ row, height }) => {
    worksheet.getRow(row + 1).height = height;
  });
  hints?.bandRows?.forEach((banda) => aplicarBanda(worksheet, banda));
  hints?.dataBars?.forEach((barra) => aplicarDataBar(worksheet, barra));
  hints?.numberFormats?.forEach(({ column, format }) => {
    worksheet.getColumn(column + 1).numFmt = format;
  });
  // Antes de `cellFills`/`cellStyles`: son el estilo BASE de las filas de datos, y cualquier hint
  // por celda (un tinte por Fundamental, un color por estado) tiene que poder ganarle.
  if (hints?.dataColumnStyles) aplicarEstiloDeColumnas(worksheet, hints.dataColumnStyles);
  hints?.cellFills?.forEach(({ row, column, fillArgb, fontArgb }) => {
    const cell = worksheet.getRow(row + 1).getCell(column + 1);
    cell.fill = fillSolido(fillArgb);
    cell.font = { name: EXCEL_FONT_FAMILY, size: 10.5, color: { argb: fontArgb } };
  });
  // Al final, para no perderse bajo el font base/banding que el loop de arriba ya aplicó a la celda.
  hints?.cellTextColors?.forEach((hint) => aplicarColorDeTexto(worksheet, hint));
  hints?.cellStyles?.forEach((hint) => aplicarEstiloDeCelda(worksheet, hint));
  // Los bordes van al FINAL: son la rejilla de la tabla y no deben perderse bajo ningún fill.
  hints?.borderedRanges?.forEach((rango) => aplicarBordes(worksheet, rango));
}

/** Ver `ExcelBandRowHint`. */
function aplicarBanda(worksheet: ExcelJS.Worksheet, banda: ExcelBandRowHint) {
  const fila = worksheet.getRow(banda.row + 1);
  for (let columna = 1; columna <= banda.columnCount; columna += 1) {
    const celda = fila.getCell(columna);
    celda.fill = fillSolido(banda.fillArgb);
    // El loop de `eachRow` no llega aquí (salta las filas vacías), así que sin esto la banda se
    // queda con el Calibri por omisión de Excel: la única fuente distinta del libro.
    celda.font = {
      name: EXCEL_FONT_FAMILY,
      size: banda.fontSize ?? 10.5,
      italic: banda.isItalic ?? false,
      color: banda.fontColorArgb ? { argb: banda.fontColorArgb } : undefined,
    };
    celda.alignment = { vertical: 'middle', wrapText: true };
  }
}

/** Ver `ExcelDataColumnStyles`. */
function aplicarEstiloDeColumnas(worksheet: ExcelJS.Worksheet, estilos: ExcelDataColumnStyles) {
  for (let fila = estilos.startRow; fila <= estilos.endRow; fila += 1) {
    const filaExcel = worksheet.getRow(fila + 1);
    estilos.columns.forEach((estilo) => {
      const celda = filaExcel.getCell(estilo.column + 1);
      if (estilo.fillArgb) celda.fill = fillSolido(estilo.fillArgb);
      celda.font = {
        ...celda.font,
        color: estilo.fontColorArgb ? { argb: estilo.fontColorArgb } : celda.font?.color,
        bold: estilo.isBold ?? celda.font?.bold,
      };
      celda.alignment = {
        ...celda.alignment,
        horizontal: estilo.horizontal ?? celda.alignment?.horizontal,
        vertical: estilo.vertical ?? celda.alignment?.vertical,
        wrapText: estilo.hasWrapText ?? celda.alignment?.wrapText,
      };
      if (estilo.numFmt) celda.numFmt = estilo.numFmt;
    });
  }
}

/** Ver `ExcelBorderedRangeHint`. */
function aplicarBordes(worksheet: ExcelJS.Worksheet, rango: ExcelBorderedRangeHint) {
  const lado: Partial<ExcelJS.Border> = { style: 'thin', color: { argb: rango.colorArgb } };
  for (let fila = rango.startRow; fila <= rango.endRow; fila += 1) {
    const filaExcel = worksheet.getRow(fila + 1);
    for (let columna = rango.startColumn; columna <= rango.endColumn; columna += 1) {
      filaExcel.getCell(columna + 1).border = {
        top: lado, left: lado, bottom: lado, right: lado,
      };
    }
  }
}

function aplicarColorDeTexto(worksheet: ExcelJS.Worksheet, hint: ExcelCellColorHint) {
  const celda = worksheet.getRow(hint.row + 1).getCell(hint.column + 1);
  celda.font = { ...celda.font, color: { argb: hint.colorArgb }, bold: hint.bold ?? celda.font?.bold };
}

/** Ver doc-comment de `ExcelSheetStyleHints.cellStyles`. */
function aplicarEstiloDeCelda(worksheet: ExcelJS.Worksheet, hint: ExcelCellStyleHint) {
  const celda = worksheet.getRow(hint.row + 1).getCell(hint.column + 1);
  if (hint.fillArgb) celda.fill = fillSolido(hint.fillArgb);
  celda.font = {
    ...celda.font,
    color: hint.fontColorArgb ? { argb: hint.fontColorArgb } : celda.font?.color,
    bold: hint.isBold ?? celda.font?.bold,
    italic: hint.isItalic ?? celda.font?.italic,
    size: hint.fontSize ?? celda.font?.size,
  };
  if (hint.hasWrapText || hint.horizontal || hint.vertical || hint.indent != null) {
    celda.alignment = {
      ...celda.alignment,
      wrapText: hint.hasWrapText ?? celda.alignment?.wrapText,
      horizontal: hint.horizontal ?? celda.alignment?.horizontal,
      vertical: hint.vertical ?? celda.alignment?.vertical,
      indent: hint.indent ?? celda.alignment?.indent,
    };
  }
  if (hint.numFmt) celda.numFmt = hint.numFmt;
}

/**
 * Bug de producción 2026-08-26 (hoja "Embudo Adopción") — barra de datos nativa de Excel sobre la
 * columna indicada, gradiente desde 0 hasta el máximo del propio rango (no un 0-100% fijo: el
 * embudo reparte 100% entre 3 filas, así que el máximo real de la columna YA es el 100% visual que
 * se busca).
 */
function aplicarDataBar(worksheet: ExcelJS.Worksheet, barra: ExcelDataBarHint) {
  const letra = letraDeColumna(barra.column);
  const ref = `${letra}${barra.startRow + 1}:${letra}${barra.endRow + 1}`;

  // `color` no está en el tipo `DataBarRuleType` de `exceljs` (hueco de sus definiciones), pero SÍ lo
  // lee su xform en tiempo de ejecución (`databar-xform.js`) — la intersección declara honestamente
  // el campo real en vez de forzar un `as` que oculte el hueco.
  const regla: ExcelJS.DataBarRuleType & { color: { argb: string } } = {
    type: 'dataBar',
    priority: 1,
    gradient: true,
    border: false,
    showValue: true,
    cfvo: [{ type: 'min' }, { type: 'max' }],
    color: { argb: barra.color },
  };

  worksheet.addConditionalFormatting({ ref, rules: [regla] });
}

/**
 * Construye y descarga un libro Excel con una hoja por cada `ExcelSheetSpec` recibido,
 * en el orden en que aparecen en `spec.sheets`. Toda hoja sin `styleHints` recibe el tema por
 * omisión (`aplicarEstiloBase`).
 */
export async function exportMultiSheetExcel(spec: ExcelReportSpec): Promise<void> {
  const workbook = new ExcelJS.Workbook();

  spec.sheets.forEach((sheetSpec) => {
    const worksheet = workbook.addWorksheet(sheetSpec.name);
    worksheet.addRows(sheetSpec.rows);
    aplicarEstiloBase(worksheet, sheetSpec.styleHints);

    if (sheetSpec.image) {
      const imageId = workbook.addImage({ base64: sheetSpec.image.base64, extension: 'png' });
      worksheet.addImage(imageId, { tl: sheetSpec.image.tl, ext: sheetSpec.image.ext });
    }
  });

  await descargarWorkbookComoExcel(workbook, normalizarNombreArchivo(spec.fileName));
}

/**
 * Hoja "Resumen Ejecutivo": título, subtítulo, periodo, filtros aplicados y la tabla de KPIs
 * en formato "KPI | Valor | Detalle". La repiten los 9 Excel de la spec de Reportería.
 */
export function buildResumenEjecutivoSheet(
  titulo: string,
  subtitulo: string,
  periodo: string,
  filtrosAplicados: string,
  kpis: ResumenEjecutivoKpi[],
): ExcelSheetSpec {
  const rows: ExcelCellValue[][] = [
    [titulo],
    [subtitulo],
    [],
    ['Periodo', periodo],
    ['Filtros aplicados', filtrosAplicados],
    [],
    ['KPI', 'Valor', 'Detalle'],
    ...kpis.map((kpi) => [kpi.label, kpi.valor, kpi.detalle ?? '']),
  ];

  return {
    name: NOMBRE_HOJA_RESUMEN_EJECUTIVO,
    rows,
    styleHints: { headerRows: [6], merges: ['A1:D1'], columnWidths: [45.66, 18.11, 43.89, 18.11] },
  };
}

/**
 * Hoja "Definiciones": glosario "Dato | Qué es | Cómo se calcula" que acompaña a los 9 Excel
 * de la spec de Reportería para que el lector entienda cada columna sin ambigüedad.
 *
 * La cuarta columna «Se compara contra» solo se agrega si ALGUNA definición trae
 * `seComparaContra` — la plantilla de Demográfico la pide, las de los otros 8 reportes no, y una
 * columna vacía en esas hojas se leería como un dato que se olvidaron de llenar.
 */
export function buildDefinicionesSheet(definiciones: DefinicionDato[]): ExcelSheetSpec {
  const llevaComparacion = definiciones.some((definicion) => !!definicion.seComparaContra);
  const rows: ExcelCellValue[][] = [
    llevaComparacion
      ? ['Dato', 'Qué es', 'Cómo se calcula', 'Se compara contra']
      : ['Dato', 'Qué es', 'Cómo se calcula'],
    ...definiciones.map((definicion) => (llevaComparacion
      ? [definicion.dato, definicion.queEs, definicion.comoSeCalcula, definicion.seComparaContra ?? '']
      : [definicion.dato, definicion.queEs, definicion.comoSeCalcula])),
  ];

  return {
    name: NOMBRE_HOJA_DEFINICIONES,
    rows,
    styleHints: {
      headerRows: [0],
      columnWidths: llevaComparacion ? [40, 47.55, 53.33, 40] : [40, 47.55, 53.33],
    },
  };
}

/** Un Fundamental del catálogo de Reconocimiento con sus Hábitos, tal como los necesita la matriz
 *  de "Todos los reconocimientos" para armar el header combinado de 2 filas. */
export interface FundamentalConHabitos {
  nombre: string;
  habitos: { nombre: string }[];
}

// ExcelJS no expone un helper de "índice de columna → letra"; se implementa localmente (algoritmo
// estándar de conversión a base-26 con dígitos A-Z, sin dígito cero).
function letraDeColumna(indiceBase0: number): string {
  let numero = indiceBase0 + 1;
  let letra = '';
  while (numero > 0) {
    const resto = (numero - 1) % 26;
    letra = String.fromCharCode(65 + resto) + letra;
    numero = Math.floor((numero - 1) / 26);
  }
  return letra;
}

/** Inverso de `letraDeColumna`: letra de columna Excel (A, B, ..., Z, AA, ...) a índice 0-based. */
function indiceDeColumna(letra: string): number {
  let indice = 0;
  for (const caracter of letra) indice = indice * 26 + (caracter.charCodeAt(0) - 64);
  return indice - 1;
}

/**
 * Rango de merge que abarca una fila entera, de `columnaInicio` a `columnaFin` (ambas 0-indexed):
 * `mergeDeFila(0, 0, 7)` -> `"A1:H1"`. Existe porque una hoja de ancho DINÁMICO no puede declarar su
 * merge como literal (el `'A1:D1'` de `buildResumenEjecutivoSheet` solo sirve porque esa hoja
 * siempre tiene 4 columnas): la hoja "Resumen Ejecutivo" del Excel Demográfico suma una columna por
 * cada OU con censo, así que su título tiene que combinarse contra el ancho calculado en tiempo de
 * export.
 */
export function mergeDeFila(fila: number, columnaInicio: number, columnaFin: number): string {
  return `${letraDeColumna(columnaInicio)}${fila + 1}:${letraDeColumna(columnaFin)}${fila + 1}`;
}

/**
 * Tarea 17 — traslada un rango de celdas tipo "A1:B1" sumándole `offsetColumnas` columnas (ej.
 * `desplazarMerge("A1:B1", 24)` -> `"Y1:Z1"`). `buildMatrizHabitosHeader` devuelve merges relativos
 * a `A1`; quien inserte esa matriz en una hoja con columnas fijas a la izquierda (como "Todos los
 * reconocimientos") usa esto para trasladarlos al offset real.
 *
 * `offsetFilas` (opcional, 0 por omisión para no cambiar a quien ya lo llamaba) hace lo mismo con
 * las filas: la matriz asume que su header empieza en la fila 1, y una hoja que le antepone un
 * banner de título/subtítulo necesita bajar esos merges tantas filas como banner tenga.
 */
export function desplazarMerge(rango: string, offsetColumnas: number, offsetFilas = 0): string {
  const [inicio, fin] = rango.split(':');
  const desplazarCelda = (celda: string): string => {
    const coincidencia = celda.match(/^([A-Z]+)(\d+)$/);
    if (!coincidencia) throw new Error(`Rango de celda inválido: "${celda}"`);
    const [, letras, fila] = coincidencia;
    return `${letraDeColumna(indiceDeColumna(letras) + offsetColumnas)}${Number(fila) + offsetFilas}`;
  };
  return `${desplazarCelda(inicio)}:${desplazarCelda(fin)}`;
}

/**
 * Header de 2 filas para la matriz de hábitos de "Todos los reconocimientos": fila superior con el
 * nombre del Fundamental combinado sobre sus columnas de Hábito, fila inferior con cada Hábito. El
 * orden de `fundamentales` es responsabilidad del caller (Tarea 17: orden del catálogo, no fijo).
 * Los `merges` devueltos usan columnas relativas a `A1` — quien inserte esta matriz en una hoja con
 * más columnas a la izquierda (Tarea 17) es responsable de trasladar el offset absoluto.
 */
export function buildMatrizHabitosHeader(fundamentales: FundamentalConHabitos[]): {
  headerRows: ExcelCellValue[][];
  merges: string[];
} {
  const filaFundamentales: ExcelCellValue[] = [];
  const filaHabitos: ExcelCellValue[] = [];
  const merges: string[] = [];
  let columna = 0;

  for (const fundamental of fundamentales) {
    const inicio = columna;
    filaFundamentales.push(fundamental.nombre);
    for (let indice = 1; indice < fundamental.habitos.length; indice += 1) filaFundamentales.push(null);

    for (const habito of fundamental.habitos) {
      filaHabitos.push(habito.nombre);
      columna += 1;
    }

    if (fundamental.habitos.length > 1) {
      merges.push(`${letraDeColumna(inicio)}1:${letraDeColumna(columna - 1)}1`);
    }
  }

  return { headerRows: [filaFundamentales, filaHabitos], merges };
}
